import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUploadPickupOrderBarcode } from '@/api/pickup-orders/queries/usePickupOrders'
import { PickupOrder } from '@/api/pickup-orders/types/PickupOrder'
import { AppDialog } from '@/components/AppDialog'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/useToast'

import { UploadArea } from '../../ReceiptUpload/components/UploadArea'

interface Props {
  order: PickupOrder
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}
export const UploadBarcodeDialog = ({ order, isOpen, onOpenChange }: Props) => {
  const { t } = useTranslation()
  const [uploadAreaKey, setUploadAreaKey] = useState(0)
  const [barcodeFile, setBarcodeFile] = useState<File | null>(null)
  const uploadMutation = useUploadPickupOrderBarcode()
  const { showSuccessToast, showErrorToast } = useToast()

  const handleClose = (open: boolean) => {
    if (!open) {
      setUploadAreaKey(k => k + 1)
    }
    onOpenChange(open)
  }

  const onSubmit = async () => {
    if (!barcodeFile) {
      showErrorToast(t('pickup.mustUploadBarcode'))
      return
    }
    try {
      await uploadMutation.mutateAsync({ id: order.id, barcodeFile: barcodeFile })
      handleClose(false)
      showSuccessToast(t('pickup.barcodeSuccess'), t('pickup.barcodeSuccessDesc'))
    } catch {
      showErrorToast(t('pickup.barcodeUploadError'))
    }
  }

  return (
    <AppDialog
      isOpen={isOpen}
      onOpenChange={handleClose}
      title={t('pickup.barcodeForPickup')}
      className='sm:max-w-2xl'
      footerActions={
        <Button loading={uploadMutation.isPending} disabled={uploadMutation.isPending} onClick={onSubmit}>
          {t('pickup.uploadBarcodeButton')}
        </Button>
      }
    >
      <div className='flex flex-col gap-4'>
        <p className='text-text-primary'>{t('pickup.barcodeDescription')}</p>
        <p className='text-sm'>{t('pickup.barcodeFromEmail')}</p>
        <p className='text-text-secondary text-sm font-medium'>{t('pickup.barcodeLooksLike')}</p>
        <div className='border-border-default bg-muted/30 flex h-24 items-center justify-center rounded-lg border-2 border-dashed'>
          <span className='text-text-muted text-xs'>{t('pickup.barcodePlaceholder')}</span>
        </div>
        <div className='flex flex-col gap-4'>
          <UploadArea maxFileCount={1} key={uploadAreaKey} onChangeFile={(file: File | null) => setBarcodeFile(file)} />
        </div>
      </div>
    </AppDialog>
  )
}
